/**
 * BLUF-1323 — capability guard for protected modules.
 *
 * Sibling of `RequireRole`. Instead of matching the session's role
 * against a hardcoded allowlist, it checks the capabilities the API
 * derived from the caller's Cognito group description
 * (`[caps: uw-view, ...]`). The API's route pre-handlers already gate
 * on the same tag — this component prevents the lazy module bundle
 * from ever being fetched for unauthorized users.
 *
 * Grant a role access to a module by editing the Cognito group
 * description, not by editing this file.
 *
 * While /api/me is still in flight we render nothing so there is no
 * flash of restricted content and no flash of the redirect.
 */

import { type ReactNode } from 'react';
import { useAuth } from './AuthContext.js';
import { AccessDeniedPage } from './AccessDeniedPage.js';

export function RequireCapability({
  capability,
  children,
}: {
  capability: string;
  children: ReactNode;
}): ReactNode {
  const { capabilities, isLoading } = useAuth();

  if (isLoading) return null;

  const needle = capability.toLowerCase();
  const held = capabilities.some((c) => c.toLowerCase() === needle);
  if (!held) return <AccessDeniedPage />;

  return <>{children}</>;
}
